/**
 * API helper for Pelmeshka.
 * Detects when backend is unavailable (e.g., on GitHub Pages static site)
 * and falls back to demo data from localStorage.
 */

import { DEMO_DATA, CATEGORY_HIERARCHY } from '../demoData';

const DEMO_MODE_STORAGE_KEY = 'pelmeshka_demo_mode';
const DEMO_TRANSACTIONS_STORAGE_KEY = 'pelmeshka_demo_transactions';
const DEMO_BUDGET_STORAGE_KEY = 'pelmeshka_demo_budget';

/**
 * Check if backend is available by attempting a health check
 */
async function isBackendAvailable() {
  try {
    const response = await fetch('/api/health', { method: 'GET', timeout: 2000 });
    return response.ok;
  } catch (err) {
    return false;
  }
}

/**
 * Initialize demo mode: seed localStorage with demo data on first visit
 */
function initializeDemoMode() {
  const isDemoMode = localStorage.getItem(DEMO_MODE_STORAGE_KEY);
  
  if (!isDemoMode) {
    // First visit: seed demo data
    localStorage.setItem(DEMO_MODE_STORAGE_KEY, 'true');
    localStorage.setItem(DEMO_TRANSACTIONS_STORAGE_KEY, JSON.stringify(DEMO_DATA.transactions));
    localStorage.setItem(DEMO_BUDGET_STORAGE_KEY, JSON.stringify(DEMO_DATA.budget));
  }
}

/**
 * Get demo transactions from localStorage
 */
function getDemoTransactions() {
  const stored = localStorage.getItem(DEMO_TRANSACTIONS_STORAGE_KEY);
  return stored ? JSON.parse(stored) : DEMO_DATA.transactions;
}

/**
 * Get demo budget from localStorage
 */
function getDemoBudget() {
  const stored = localStorage.getItem(DEMO_BUDGET_STORAGE_KEY);
  return stored ? JSON.parse(stored) : DEMO_DATA.budget;
}

/**
 * Get divisions (categories) for demo mode
 */
/**
 * Get demo categories extracted from demo transactions
 */
function getDemoCategories() {
  const transactions = getDemoTransactions();
  const categoryMap = new Map();

  // Extract unique categories from transactions
  transactions.forEach(tx => {
    const category = tx.final_category || tx.manual_category;
    if (category && !categoryMap.has(category)) {
      const hierarchy = CATEGORY_HIERARCHY[category];
      categoryMap.set(category, {
        category_name: category,
        group_name: hierarchy ? hierarchy.group : '',
        fix_variable: 'Variable',
        exclude: false
      });
    }
  });

  return Array.from(categoryMap.values());
}

/**
 * Wrapper around fetch that intercepts API calls and serves demo data when backend is unavailable
 */
export async function fetchWithDemoFallback(url, options = {}) {
  // Always try backend first
  try {
    const response = await fetch(url, options);
    // Return response regardless of status (caller handles errors)
    return response;
  } catch (err) {
    // Backend unreachable - only serve demo data on pelmeshka.ch
    const isWebsite = typeof window !== 'undefined' && window.location.hostname === 'pelmeshka.ch';
    
    if (!isWebsite) {
      // Desktop app or localhost: let the error propagate
      throw err;
    }
    
    // Website only: serve demo data as fallback
    console.log('[Pelmeshka] Backend unavailable, serving demo data');
    
    if (url.includes('/api/transactions')) {
      return new Response(
        JSON.stringify(getDemoTransactions()),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    if (url.includes('/api/budgets')) {
      const budget = getDemoBudget();
      const categories = budget.map(b => b.category);
      const months = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      const values = {};
      const avg_monthly_spend_last_6m = {};
      categories.forEach(cat => {
        values[cat] = {};
        months.forEach(month => {
          values[cat][month] = budget.find(b => b.category === cat)?.amount || null;
        });
        avg_monthly_spend_last_6m[cat] = budget.find(b => b.category === cat)?.amount || 0;
      });
      const entries = [];
      categories.forEach(cat => {
        const budgetAmount = budget.find(b => b.category === cat)?.amount || 0;
        months.forEach(month => {
          entries.push({
            category: cat,
            budget_month: month,
            amount: budgetAmount,
            avg_monthly_spend_last_6m: budgetAmount,
            updated_at: new Date().toISOString()
          });
        });
      });
      return new Response(
        JSON.stringify({
          months,
          categories,
          entries,
          values,
          avg_monthly_spend_last_6m
        }),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    if (url.includes('/api/categories-with-groups')) {
      return new Response(
        JSON.stringify(getDemoCategories()),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    if (url.includes('/api/settings')) {
      return new Response(
        JSON.stringify({}),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    if (url.includes('/api/keyw2cat')) {
      return new Response(
        JSON.stringify([]),
        { status: 200, headers: { 'Content-Type': 'application/json' } }
      );
    }
    
    // No demo data for this endpoint, propagate error
    throw err;
  }
}

/**
 * Check if currently in demo mode
 */
export function isDemoMode() {
  // Only demo mode on the website (pelmeshka.ch), not on desktop app
  if (typeof window !== 'undefined' && window.location.hostname === 'pelmeshka.ch') {
    return localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true';
  }
  return false;
}

/**
 * Reset demo mode (clear demo data from localStorage)
 */
export function resetDemoMode() {
  localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
  localStorage.removeItem(DEMO_TRANSACTIONS_STORAGE_KEY);
  localStorage.removeItem(DEMO_BUDGET_STORAGE_KEY);
}

/**
 * Initialize demo mode on app load
 */
export function initDemoModeOnLoad() {
  // Check backend availability
  isBackendAvailable().then(available => {
    if (!available) {
      console.log('[Pelmeshka] Backend not available, initializing demo mode');
      initializeDemoMode();
    }
  });
}
